// runtime-draw-utils.js — small canvas + formatting helpers shared by every
// overlay renderer (main, vertical, boards): safe ctx state wrapper, colour
// conversion, text clipping, ball pills, rounded rects, SR/econ formatting and
// the cached intro payload. Split from runtime-core.js.
import { validateIntroPayload } from './intro-sequence.js';

// Save context state, run a fn, then restore — keeps text alignment/baseline pristine.
export function withCtx(ctx, fn) {
  ctx.save();
  try { fn(); } finally { ctx.restore(); }
}

// Convert "#rrggbb" + alpha to "rgba(r,g,b,a)"
export function rgba(hex, a) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h[0]+h[0] + h[1]+h[1] + h[2]+h[2];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return "rgba(" + r + "," + g + "," + b + "," + a + ")";
}

export function getCachedIntroPayload(anim) {
  if (!anim) return null;
  if (!anim._introPayloadChecked) {
    const intro = validateIntroPayload(anim.payload);
    if (!intro.ok) {
      console.warn("Intro data incomplete:", intro.error);
      anim._introPayload = null;
      anim._introPayloadChecked = true;
      return null;
    }
    anim._introPayload = intro.value;
    anim._introPayloadChecked = true;
  }
  return anim._introPayload || null;
}

export function clipText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  if (ctx.measureText("…").width > maxWidth) return "";
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid; else hi = mid - 1;
  }
  return text.slice(0, lo) + "…";
}

export function drawBallPill(ctx, x, y, size, b) {
  let bg, fg = "#fff", txt;
  if (b.type === "wicket")     { bg = "#dc2626"; txt = "W"; }
  else if (b.type === "wide")  { bg = "#0891b2"; txt = b.runs + "Wd"; }
  else if (b.type === "nb")    { bg = "#be185d"; txt = b.runs + "Nb"; }
  else if (b.type === "b")     { bg = "#52525b"; txt = b.runs + "b"; }
  else if (b.type === "lb")    { bg = "#52525b"; txt = b.runs + "lb"; }
  else if (b.type === "dot")   { bg = "#27272a"; txt = "•"; fg = "#9ca3af"; }
  else if (b.runs === 4)       { bg = "#ea580c"; txt = "4"; }
  else if (b.runs === 6)       { bg = "#6d28d9"; txt = "6"; }
  else                         { bg = "#1d4ed8"; txt = String(b.runs); }
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = bg;
  ctx.beginPath(); ctx.arc(x + size / 2, y, size / 2, 0, Math.PI * 2); ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.fillStyle = fg;
  const fontSize = txt.length >= 3 ? 8 : (txt.length >= 2 ? 10 : 13);
  ctx.font = "900 " + fontSize + "px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(txt, x + size / 2, y + 1);
  ctx.restore();
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

export function sr(runs, balls) { return balls === 0 ? "—" : ((runs / balls) * 100).toFixed(1); }

export function econ(runs, balls) { return balls === 0 ? "—" : ((runs / balls) * 6).toFixed(2); }

export function warmUpFonts() {
  try {
    if (!document.fonts || !document.fonts.load) return;
    ["900 48px Outfit", "800 24px Outfit", "700 18px Outfit", "900 24px Outfit"]
      .forEach(f => { try { document.fonts.load(f); } catch (_) {} });
  } catch (_) {}
}
